import React, { useCallback, useEffect, useRef, useState } from "react";

import { FilterItem, updateJoinOperatorInFirstConditions } from "./TableFIlterScripts";
import { Button } from "../../../../BP2Components";

import TableFiltersNavButtons from "./TableFiltersNavButtons";
import TableFilterEditor from "./TableFilterEditor";
import IntlMessages from "../../../../../util/IntlMessages";
import TableFilters from "./TableFilters";

import "../../../../BP2Components/Table/table.less";
import {
   addListFilter,
   deleteFilter,
   editListFilter,
   duplicateFilter,
   getListFilters,
   addGroup,
   getListV3WithFilter,
   deleteGroup,
   duplicateGroup,
   editListGroup,
   modifyGroup,
} from "../../../../../services/restAPI/ListsV2";
import { useTableListContext } from "../../../../../context/TableListContext/TableListContext";
import { listColumns } from "./TableFiltersColumns";
import ConfirmModal from "./ConfirmModal/ConfirmModal";
import deleteIcon from "../../../../../assets/icons/delete.svg";
import CreateGroupModal from "./CreateGroupModal/CreateGroupModal";
import { useDispatch } from "react-redux";
import { setWidgetInSheetParams } from "../../../../../appRedux/actions/Boards";

interface TableFilterProps {
   value: any;
   listId: string;
   setActiveFilter: any;
   defaultFilterIdFromSettings?: string | false;
   setWidgetSettings: any;
   openButtonLabel?: React.ReactNode;
   filtersOpen?: boolean;
   setFiltersOpen?: (open: boolean) => void;
   disableStoreSync?: boolean;
}

export const MODALS = {
   FILTER_EDITOR: "FILTER_EDITOR",
   CONFIRM_DELETE: "CONFIRM_DELETE",
   CREATE_GROUP: "CREATE_GROUP",
};

const TableFiltersController: React.FC<TableFilterProps> = ({
   listId,
   defaultFilterIdFromSettings,
   value,
   setWidgetSettings,
   openButtonLabel,
   filtersOpen,
   setFiltersOpen,
   disableStoreSync = false,
}) => {
   const [internalFiltersVisible, setInternalFiltersVisible] = useState<boolean>(false);
   const isControlled = filtersOpen !== undefined;
   const areFiltersVisible = filtersOpen ?? internalFiltersVisible;
   const setAreFiltersVisible = setFiltersOpen ?? setInternalFiltersVisible;

   const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
   const [modalType, setModalType] = useState(null);
   const [actionId, setActionId] = useState<string | null>(null);

   const [currentFilter, setCurrentFilter] = useState<FilterItem>();

   const [openMenuId, setOpenMenuId] = useState<string | null>(null);
   const menuNode = useRef<HTMLDivElement | null>(null);

   const {
      setActiveFilter,
      setEditedListFilters,
      listFilters,
      setListFilters,
      currentActiveFilter,
      activeTab,
      setActiveTab,
      totalElements,
      setTotalElements,
      setGroupsIsChanged,
      activeFilter,
   } = useTableListContext();

   const dispatch = useDispatch();

   const activeFilterRef = useRef<string | boolean>(defaultFilterIdFromSettings || activeFilter);
   useEffect(() => {
      activeFilterRef.current = activeFilter;
   }, [activeFilter]);

   useEffect(() => {
      if (!defaultFilterIdFromSettings) {
         setActiveFilter(false);
         activeFilterRef.current = false;
      }
   }, [defaultFilterIdFromSettings]);

   const isFilterTab = activeTab === 1;

   const updateFilterInState = (updated: any) => {
      setListFilters((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));

      setEditedListFilters((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
   };

   const editByTab = (data: any) =>
      isFilterTab ? editListFilter(listId, data) : modifyGroup(listId, data);

   const refreshByTab = async () => {
      if (isFilterTab) {
         await refreshFilters();
      } else {
         setGroupsIsChanged(true);
      }
   };

   useEffect(() => {
      setActiveFilter(defaultFilterFromSettings?.id ?? false);
   }, []);

   const defaultFilterFromSettings = listFilters?.find(
      (filter) => String(filter.id) === String(defaultFilterIdFromSettings),
   );

   const setDefaultFilterForList = (defaultFilterId: string) => {
      if (disableStoreSync) return;
      const nextSeries = (value?.tableSeries ?? []).map((serie) => {
         if (String(serie.dataId) === String(listId)) {
            return { ...serie, defaultFilterId };
         }
         return serie;
      });
      const nextSettings = { ...value, tableSeries: nextSeries };
      setWidgetSettings(nextSettings);
      dispatch(setWidgetInSheetParams(nextSettings));
   };

   const closeModal = () => {
      setModalType(null);
      setIsModalOpen(false);
      setActionId(null);
   };

   const showModal = (type) => {
      setOpenMenuId(null);
      setModalType(type);
   };

   useEffect(() => {
      if (!openMenuId) return;
      const handleClickOutside = (event: MouseEvent) => {
         if (menuNode.current && !menuNode.current.contains(event.target as Node)) {
            setOpenMenuId(null);
         }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
   }, [openMenuId]);

   const updateFilter = (updated) => {
      setListFilters((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
      setEditedListFilters((prev) => prev.map((f) => (f.id === updated.id ? updated : f)));
   };

   const applyActiveFlagToFilters = (filtersArray) => {
      const effectiveActiveId = activeFilterRef.current;
      if (!effectiveActiveId) return filtersArray;

      return filtersArray.map((filterItem) => {
         return {
            ...filterItem,
            active: String(filterItem.id) === String(effectiveActiveId),
         };
      });
   };

   const refreshFilters = async () => {
      try {
         const result = await getListFilters(listId);
         if (result?.data) {
            const selectedFilter = applyActiveFlagToFilters(result.data);
            setListFilters(selectedFilter);
            setEditedListFilters(selectedFilter);
            const activeItem = selectedFilter.find((f) => f.active);
            if (activeItem) {
               setActiveFilter(activeItem.id);
            } else {
               setActiveFilter(false);
               activeFilterRef.current = false;
            }
         }
      } catch (err) {
         console.error(err);
      }
   };

   const handleDuplicate = async () => {
      if (!openMenuId) return;
      try {
         if (activeTab === 1) {
            const res = await duplicateFilter(listId, openMenuId);
            if (res?.status === 200) {
               setOpenMenuId(null);
               await refreshFilters();
            }
         } else {
            const res = await duplicateGroup(listId, openMenuId);
            if (res?.status === 200) {
               setOpenMenuId(null);
               setGroupsIsChanged(true);
            }
         }
      } catch (err) {
         console.error(err);
      }
   };

   const handleDelete = async () => {
      if (!actionId) return;
      const activeField = listFilters.find((f) => f.active);
      try {
         if (activeField && activeField.id === actionId) {
            const resModify =
               activeTab === 1
                  ? await editListFilter(listId, { ...activeField, active: false })
                  : await modifyGroup(listId, { ...activeField, active: false });
            if (resModify?.data) {
               updateFilter(resModify.data);
               setActiveFilter(false);
            }
         }
         if (activeTab === 1) {
            const res = await deleteFilter(listId, actionId);
            if (res?.status === 204) {
               await refreshFilters();
               closeModal();
            }
         } else {
            const res = await deleteGroup(listId, actionId);
            if (res?.status === 200) {
               setGroupsIsChanged(true);
               closeModal();
            }
         }
      } catch (err) {
         console.error(err);
      }
   };

   const deactivateActiveFilter = async (clear = true) => {
      const active = listFilters.find((f) => f.active);
      if (!active) return;

      const updated = { ...active, active: false };

      const res = await editByTab(updated);

      if (res?.data) {
         updateFilterInState(res.data);
      }
      if (clear) {
         activeFilterRef.current = false;
         setActiveFilter(false);
         if (isFilterTab && defaultFilterIdFromSettings) {
            setDefaultFilterForList("");
         }
      }
   };

   const handleEditByTabType = (listId, data) => {
      if (activeTab === 1) {
         return editListFilter(listId, data);
      } else {
         return modifyGroup(listId, { ...data, profileId: actionId });
      }
   };

   const handleFilterEdit = async (filter, value, field) => {
      if (field !== "active") {
         setEditedListFilters((prev) =>
            prev.map((item) => (item.id === filter.id ? { ...item, [field]: value } : item)),
         );
         return;
      }
      if (value === false) {
         const res = await handleEditByTabType(listId, { ...filter, active: false });
         if (res?.data) {
            updateFilter(res.data);
            setActiveFilter(false);
            if (isFilterTab && defaultFilterIdFromSettings) {
               setDefaultFilterForList("");
            }
         }
         return;
      }

      await deactivateActiveFilter(false);
      const res = await handleEditByTabType(listId, { ...filter, active: true });
      if (res?.data) {
         updateFilter(res.data);
         setActiveFilter(res.data.id);
         if (activeTab === 1) {
            setDefaultFilterForList(res.data.id);
         }
      }
   };

   const handleSaveFilter = async (filter) => {
      const isExisting = listFilters.some((f) => f.id === filter.id);

      if (isExisting) {
         await handleEditByTabType(listId, filter);
      } else {
         await deactivateActiveFilter();
         const res = await addListFilter(listId, { ...filter, active: true });
         if (res?.data) {
            setListFilters((prev) => [...prev, res.data]);
            setEditedListFilters((prev) => [...prev, res.data]);
            setActiveFilter(res.data.id);
            setDefaultFilterForList(res.data.id);
            closeModal();
         }
      }
      await refreshByTab();
      setOpenMenuId(null);
   };

   const handleSaveGroup = async (filter) => {
      const isEmptyGroup = !totalElements;
      const isExisting = listFilters.some((f) => f.id === filter.id);
      try {
         if (isExisting) {
            const res = await editListGroup(listId, { ...filter, profileId: actionId });
            if (res?.status === 200) {
               setGroupsIsChanged(true);
               closeModal();
            }
         } else {
            const res = await addGroup(
               listId,
               { ...filter, profileId: actionId, active: false },
               isEmptyGroup,
            );
            if (res?.status === 200) {
               setGroupsIsChanged(true);
               closeModal();
            }
         }
      } catch (err) {
         console.error(err);
      }
   };

   const getFilterObjectCount = useCallback(() => {
      getListV3WithFilter(listId, 1, 0, actionId).then((res) => {
         setTotalElements(res.data.meta.totalElements);
      });
   }, [listId, actionId]);

   const handleActiveTab = async (tab) => {
      const nextTab = Number(tab);

      try {
         await deactivateActiveFilter();
      } catch (err) {
         console.error(err);
         activeFilterRef.current = false;
         setActiveFilter(false);
      }

      setActiveTab(nextTab);
   };

   useEffect(() => {
      if (!listId) return;
      if (!value?.showListFilters) return;

      refreshFilters();
   }, [listId, value?.showListFilters]);

   return (
      <div className="table-filters-main-container">
         {areFiltersVisible && (
            <div className="table-filters-content">
               {!isControlled && (
                  <div className="open-close-button-container">
                     <Button
                        onClick={() => setAreFiltersVisible(false)}
                        className="close-button"
                        buttonType="primary"
                     >
                        <IntlMessages id="table.closeFilters" />
                        <span style={{ marginLeft: "1rem" }} className="material-symbols-outlined">
                           keyboard_arrow_up
                        </span>
                     </Button>
                  </div>
               )}
               <div className="table-filters-nav-buttons">
                  <TableFiltersNavButtons activeTab={activeTab} setActiveTab={handleActiveTab} />
               </div>
               <div className="table-filters">
                  <TableFilters
                     columns={listColumns(
                        showModal,
                        setCurrentFilter,
                        handleFilterEdit,
                        openMenuId,
                        setOpenMenuId,
                        menuNode,
                        setActionId,
                        handleDuplicate,
                        activeTab,
                     )}
                     rows={updateJoinOperatorInFirstConditions(listFilters)}
                     setEditedListFilters={setEditedListFilters}
                     setCurrentFilter={setCurrentFilter}
                     setIsModalOpen={setIsModalOpen}
                  />
               </div>
            </div>
         )}
         <div
            className={`open-close-button-container ${
               !areFiltersVisible && activeFilter && "closed"
            }`}
         >
            {!areFiltersVisible && activeFilter && (
               <div className="current-filter-preview">
                  <span className="current-filter-preview__label">
                     <IntlMessages id="table.selectedFilter" />
                  </span>
                  <span className="current-filter-preview__title">
                     {(currentActiveFilter ?? defaultFilterFromSettings)?.name}
                  </span>
                  <Button
                     onClick={() => deactivateActiveFilter()}
                     style={{ width: "32px", height: "32px", marginTop: "4px" }}
                  >
                     <span style={{ fontSize: "24px" }} className="material-symbols-outlined">
                        close
                     </span>
                  </Button>
               </div>
            )}
            {!isControlled && (
               <Button
                  buttonType="primary"
                  style={
                     areFiltersVisible
                        ? { backgroundColor: "#CBD5E1", border: "none", cursor: "not-allowed" }
                        : { border: "none" }
                  }
                  onClick={() => setAreFiltersVisible(true)}
               >
                  {openButtonLabel ?? (
                     <span className="material-symbols-outlined">filter_list</span>
                  )}
               </Button>
            )}
         </div>
         {/* // FILTER EDITOR */}
         {(modalType === MODALS.FILTER_EDITOR || isModalOpen) && (
            <TableFilterEditor
               activeTab={activeTab}
               isOpen
               setIsOpen={closeModal}
               currentFilter={currentFilter}
               setCurrentFilter={setCurrentFilter}
               handleSaveFilter={activeTab === 1 ? handleSaveFilter : handleSaveGroup}
               topBarTitle={
                  <IntlMessages
                     id={`${activeTab === 1 ? "modalEdit.edit_filter" : "modalEdit.edit_group"}`}
                  />
               }
            />
         )}
         {modalType === MODALS.CONFIRM_DELETE && (
            <ConfirmModal
               title={
                  <IntlMessages
                     id={`${activeTab === 1 ? "modalConfirm.remove_filter" : "modalConfirm.remove_group"}`}
                  />
               }
               description={<IntlMessages id="modalConfirm.remove_filter_description" />}
               confirmLabel={<IntlMessages id="global.yes_delete" />}
               onCancel={closeModal}
               onConfirm={handleDelete}
               icon={deleteIcon}
               isOpen
            />
         )}
         {modalType === MODALS.CREATE_GROUP && (
            <CreateGroupModal
               onCancel={closeModal}
               onConfirm={handleSaveGroup}
               isOpen
               currentFilter={currentFilter}
               totalElements={totalElements}
               getFilterObjectCount={getFilterObjectCount}
               topBarTitle={<IntlMessages id="global.create_group" />}
            />
         )}
      </div>
   );
};

export default TableFiltersController;
